import React, { FormEvent, useContext, useEffect, useMemo, useState } from 'react';

import {
  Money as MoneyModel,
  ReservationStatus,
  TransactionType,
  UserPlan,
} from '../../../../api-client';
import { createReservationManager } from '../../../../di';
import { useErrorCallback, useProfileData } from '../../../../hooks';
import { Button, InputMoney, Loader, Money, PrimaryButton } from '../../../common';
import { PrimaryButtonColor } from '../../../common/buttons/decorators/PrimaryButton/PrimaryButton';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';

import './ProjectReserve.scoped.scss';

const ProjectReserve = () => {
  const { project, reservation, reservationLoading } = useContext(DetailedProjectContext);
  const profileData = useProfileData();
  const handleError = useErrorCallback();
  const [loading, setLoading] = useState(false);
  const [currentReservation, setCurrentReservation] = useState(reservation ?? null);

  useEffect(() => {
    setCurrentReservation(reservation ?? null);
  }, [reservation]);

  const minInvestment = project.accounting.minInvestment;
  const [money, setMoney] = useState<MoneyModel>(new MoneyModel(minInvestment.amount, 'RUB'));

  const isPremium = profileData.userPlan === UserPlan.CONOMICA_PLUS;

  const available: MoneyModel | null = project.reservationLimit
    ? new MoneyModel(
        project.reservationLimit.amount - (project.accounting.reservedSum?.amount ?? 0),
        project.reservationLimit.currencyCode
      )
    : null;

  const commission = useMemo((): MoneyModel => {
    const settings = project.feeSettings.find(
      (item) => item.transactionType === TransactionType.INVESTMENT_FEE
    );
    if (settings) {
      if (settings.percent) {
        return new MoneyModel((money.amount * settings.percent) / 100, 'RUB');
      } else if (settings.money) {
        return settings.money;
      }
    }
    return new MoneyModel(0, 'RUB');
  }, [money.amount]);

  const total = useMemo((): MoneyModel => {
    return new MoneyModel(money.amount + commission.amount, money.currencyCode);
  }, [money, commission]);

  const disabledCondition =
    !isPremium ||
    loading ||
    money.amount < minInvestment.amount ||
    (available !== null && money.amount > available.amount) ||
    money.amount + commission.amount > profileData.balance.amount;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    try {
      const manager = await createReservationManager();
      const result = await manager.create(project.uuid, money);
      setCurrentReservation(result);
    } catch (error) {
      if (error instanceof Error) {
        await handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="detailed-project-reserve">
      <div className="detailed-project-reserve__container">
        {reservationLoading ? (
          <Loader size="small" overlay={false} />
        ) : (
          <>
            {available && (
              <div className="detailed-project-reserve__money detailed-project-reserve__money--additional">
                <div>Доступно для резервации:</div>
                <div>
                  <Money money={available} />
                </div>
              </div>
            )}
            <div className="detailed-project-reserve__money detailed-project-reserve__money--additional">
              <div>Минимальная сумма резервации:</div>
              <div>
                <Money money={minInvestment} />
              </div>
            </div>
            <div className="detailed-project-reserve__money detailed-project-reserve__money--additional">
              <div>Средств на балансе:</div>
              <div>
                <Money money={profileData.balance} />
              </div>
            </div>

            {currentReservation?.status === ReservationStatus.ACTIVE ? (
              <div className="detailed-project-reserve__reserved-box">
                Вы уже совершили резервацию участия в этом проекте на сумму&nbsp;
                <Money
                  money={
                    new MoneyModel(
                      Number(currentReservation.money.amount),
                      currentReservation.money.currencyCode
                    )
                  }
                />
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="detailed-project-reserve__input">
                  <div className="detailed-project-reserve__input-additional">
                    Введите сумму резервирования:
                  </div>
                  <InputMoney money={money} onValueChanged={setMoney} disabled={!isPremium} />
                </div>
                <div className="detailed-project-reserve__money">
                  <div>Комиссия</div>
                  <div>
                    <Money money={commission} />
                  </div>
                </div>
                <div className="detailed-project-reserve__money">
                  <div>Итого</div>
                  <div>
                    <Money money={total} />
                  </div>
                </div>
                {isPremium ? (
                  <>
                    <PrimaryButton
                      expanded
                      large
                      color={PrimaryButtonColor.GREEN}
                      className={'detailed-project-reserve__button'}
                    >
                      <Button disabled={disabledCondition} type="submit">
                        Резервировать
                      </Button>
                    </PrimaryButton>
                    <div className="detailed-project-reserve__rules">
                      <a href="#">Правила резервирования участия в сделке</a>
                    </div>
                  </>
                ) : (
                  <div className="detailed-project-reserve__non-premium">
                    Резервирование средств доступно только Премиум пользователям
                  </div>
                )}
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectReserve;
